import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useRef } from 'react';

import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import FilterList from '@mui/icons-material/FilterList';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import { Box, ButtonBase, FormControlLabel, Switch, useTheme } from '@mui/material';
import { flexRender } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { usePostHog } from 'posthog-js/react';
import { useSearchParams } from 'react-router';

import Button from '@/components/Buttons/Button';
import Tooltip from '@/components/Tooltip';
import { SHOW_SELECTED_URL_PARAM } from '@/constants/constants';
import { Z_INDEX } from '@/constants/z-index';
import useSelectionFunctions from '@/features/SymbolSelector/hooks/useSelectionFunctions';
import { FILTER_DRAWER_WIDTH } from '@/pages/Catalog/CatalogTable/constants';
import TableSearch from '@/pages/Catalog/CatalogTable/TableSearch';
import { extractExistingParams } from '@/pages/Catalog/extractExistingParams';
import type { CatalogItem } from '@/types/catalog';

import { useFilter } from '../contexts/filter/useFilter';
import CatalogTableRow from './CatalogTableRow';
import DisplaySettingsPopper from './DisplaySettings';
import SelectAllButton from './SelectAllButton';
import TableFilters from './TableFilters';

import type { HeaderGroup, Table } from '@tanstack/react-table';

const CatalogTable = ({
  filterOpen,
  setFilterOpen,
  table,
  isForwardCurves,
  expandedData,
  setExpandedData,

  setShowDiscontinued,
  showDiscontinued
}: {
  filterOpen: boolean;
  setFilterOpen: (filterOpen: boolean) => void;
  table: Table<CatalogItem>;
  isForwardCurves: boolean;
  expandedData: boolean;
  setExpandedData: (newValue: boolean) => void;

  showDiscontinued: boolean;
  setShowDiscontinued: Dispatch<SetStateAction<boolean>>;
}) => {
  const { palette } = useTheme();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { noneSelected } = useSelectionFunctions();
  const [searchParams] = useSearchParams();
  const rows = table.getRowModel().rows;
  const { filters, setFilter, isAnyFilterApplied } = useFilter();

  // oxlint-disable-next-line react-hooks-js/incompatible-library -- TanStack Virtual powers catalog row windowing.
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 23,
    getScrollElement: () => tableContainerRef.current,
    overscan: 5
  });

  const postHog = usePostHog();

  useEffect(() => {
    postHog.capture('catalogue_filter', extractExistingParams(searchParams));
  }, [postHog, searchParams]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          mb: 1
        }}
      >
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
          <Button
            size='small'
            sx={{ width: FILTER_DRAWER_WIDTH - 32, mx: 2, height: 33 }}
            variant='outlined'
            onClick={() => {
              setFilterOpen(!filterOpen);
            }}
          >
            <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
              <FilterList sx={{ mr: 0.6 }} />
              {isAnyFilterApplied ? (
                <>
                  Filters Applied
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      backgroundColor: 'primary.main',
                      mx: 1
                    }}
                  />
                </>
              ) : (
                'Filter'
              )}
            </Box>
          </Button>
          <DisplaySettingsPopper
            expandedData={expandedData}
            isForwardCurves={isForwardCurves}
            setExpandedData={setExpandedData}
            setShowDiscontinued={setShowDiscontinued}
            showDiscontinued={showDiscontinued}
            table={table}
          />
          {!isForwardCurves && (
            <FormControlLabel
              control={
                <Switch
                  checked={filters[SHOW_SELECTED_URL_PARAM]}
                  disabled={noneSelected}
                  onChange={() => {
                    setFilter(SHOW_SELECTED_URL_PARAM, !filters[SHOW_SELECTED_URL_PARAM]);
                  }}
                />
              }
              label='Show selected data'
            />
          )}
          <SelectAllButton
            isForwardCurves={isForwardCurves}
            table={table}
          />
        </Box>
        <TableSearch tabId={`catalog-search-${isForwardCurves ? 'forward-curves' : 'spot'}`} />
      </Box>
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <TableFilters
          expandedData={expandedData}
          filterOpen={filterOpen}
          height='100%'
          setFilterOpen={setFilterOpen}
          table={table}
        />
        <Box
          sx={{
            overflow: 'auto',
            flex: 1,
            minHeight: 0,
            ml: filterOpen ? 0 : `${-FILTER_DRAWER_WIDTH}px`,
            transition: 'marginLeft 0.5s ease'
          }}
        >
          {rows.length ? (
            <Box
              ref={tableContainerRef}
              sx={{
                height: '100%',
                overflowY: 'auto',
                position: 'relative',
                zIndex: Z_INDEX.sticky
              }}
            >
              <Box sx={{ display: 'grid' }}>
                <Box
                  sx={{
                    display: 'grid',
                    position: 'sticky',
                    top: 0,
                    zIndex: Z_INDEX.raised,
                    background: palette.background.paper
                  }}
                >
                  {table.getHeaderGroups().map((headerGroup: HeaderGroup<CatalogItem>) => (
                    <Box
                      key={headerGroup.id}
                      sx={{ display: 'flex', padding: '8px', paddingLeft: '16px' }}
                    >
                      {headerGroup.headers.map(header => (
                        <Box
                          key={header.id}
                          sx={{
                            display: 'flex',
                            width: header.getSize(),
                            paddingBottom: '0',
                            paddingRight: '8px',
                            justifyContent: ['Last', 'Change'].includes(
                              header.column.columnDef.header as string
                            )
                              ? 'flex-end'
                              : 'flex-start'
                          }}
                        >
                          <ButtonBase
                            disableRipple
                            sx={{
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              border: 'none',
                              color: 'inherit',
                              background: 'transparent',
                              fontFamily: 'inherit',
                              padding: 0,
                              margin: 0,
                              fontWeight: 700,
                              fontSize: '16px'
                            }}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {header.column.columnDef.header !== 'AdditionalItem' &&
                              flexRender(header.column.columnDef.header, header.getContext())}
                            {header.column.columnDef.header === 'Info' && (
                              <Tooltip title='Index information pages provide details of the methodology behind the index.'>
                                <InfoOutlined sx={{ ml: 0.5, fontSize: 12 }} />
                              </Tooltip>
                            )}
                            {(header.column.columnDef.header !== 'Info' &&
                              header.column.columnDef.header !== 'AdditionalItem' &&
                              header.column.id !== 'Symbol' &&
                              {
                                asc: (
                                  <ArrowUpward
                                    fontSize='small'
                                    sx={{ ml: 0.5 }}
                                  />
                                ),
                                desc: (
                                  <ArrowDownward
                                    fontSize='small'
                                    sx={{ ml: 0.5 }}
                                  />
                                )
                              }[header.column.getIsSorted() as string]) ?? (
                              <UnfoldMore
                                fontSize='small'
                                sx={{ ml: 0.5 }}
                              />
                            )}
                          </ButtonBase>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
                <Box sx={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}>
                  {rowVirtualizer.getVirtualItems().map(virtualRow => {
                    const row = rows[virtualRow.index];
                    return (
                      <CatalogTableRow
                        key={virtualRow.key}
                        isForwardCurves={isForwardCurves}
                        row={row}
                        virtualRow={virtualRow}
                      />
                    );
                  })}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              Nothing to display here. Try changing your filters.
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default CatalogTable;
